import _ from "lodash";

// Constants
import {
  BROWSING_THE_INTERNET,
  WRITING,
  STREAMING,
  LOADING,
} from "Constants/stringConstants";

const assignDepthToPrompt = prompts => {
  const nodes = [...prompts];

  const calculateDepth = (nodes, nodeId) => {
    const node = _.find(nodes, n => n.id === nodeId);
    const { children } = node || {};

    if (!node) return 0;
    if (_.isEmpty(children)) return 1;

    const childrenDepths = _.map(children, childId =>
      calculateDepth(nodes, childId)
    );

    return 1 + Math.max(...childrenDepths);
  };

  _.forEach(nodes, node => {
    node.depth = calculateDepth(nodes, node.id);
  });

  return nodes;
};

const sortPrompts = prompts => {
  return _.orderBy(
    prompts,
    [
      prompt => {
        const { promptType, parent } = prompt || {};
        return promptType === "display" && _.isNil(parent) ? 0 : 1;
      },
      "displaySequence",
    ],
    ["asc", "asc"]
  );
};

const sortPromptsAndChildren = prompts => {
  const promptsWithSortedChildren = _.map(prompts, prompt => {
    const { children: childrenIds } = prompt || {};

    const children = sortPrompts(
      _.compact(_.map(childrenIds, id => _.find(prompts, { id })))
    );

    const updatedChildrenIds = _.map(children, "id");

    return {
      ...prompt,
      children: !_.isEmpty(updatedChildrenIds)
        ? updatedChildrenIds
        : childrenIds,
    };
  });

  return sortPrompts(promptsWithSortedChildren);
};

export const getTopPrompts = params => {
  const {
    prompts = [],
    promptCountToShow = 4,
    selectedLearningTool,
    isAITutor,
  } = params || {};

  if (_.isEmpty(prompts)) return [];

  let updatedPrompts = _.cloneDeep(prompts);

  if (isAITutor) {
    updatedPrompts = assignDepthToPrompt(updatedPrompts);
    updatedPrompts = sortPromptsAndChildren(updatedPrompts);

    const genericPrompts = _.find(prompts, {
      action: "generic_tutor",
    });

    if (!_.isEmpty(genericPrompts)) {
      const filteredPrompts = _.filter(updatedPrompts, {
        promptType: "display",
        depth: 2,
      });

      if (!_.isEmpty(filteredPrompts)) updatedPrompts = filteredPrompts;

      const genericPromptChildrenPrompts = _.filter(updatedPrompts, prompt => {
        return _.includes(genericPrompts?.children, prompt.id);
      });

      if (!_.isEmpty(genericPromptChildrenPrompts)) {
        updatedPrompts = genericPromptChildrenPrompts;
      }
    } else {
      const filteredPrompts = _.filter(updatedPrompts, {
        promptType: "display",
        depth: 2,
      });

      if (!_.isEmpty(filteredPrompts)) updatedPrompts = filteredPrompts;
    }

    if (!_.isEmpty(selectedLearningTool)) {
      const { key, isAddedFromPrompt } = selectedLearningTool || {};

      updatedPrompts = _.map(updatedPrompts, prompt => {
        const isModalityMatch =
          prompt?.metadata?.modality === key && !isAddedFromPrompt;

        return {
          ...prompt,
          isModalityMatch,
        };
      });
    }

    const result = _.chain(updatedPrompts)
      .orderBy(["isModalityMatch", "displaySequence"], ["desc", "asc"])
      .take(promptCountToShow)
      .value();

    return result;
  } else {
    const filteredPrompts = _.filter(prompts, prompt => {
      const hasChildren = !_.isEmpty(_.get(prompt, "children"));
      const hasDisplayText = !_.isEmpty(_.get(prompt, "displayText"));

      return !hasChildren && hasDisplayText;
    });

    return _.take(filteredPrompts, promptCountToShow);
  }
};

export const getIsAIResponseLoading = state => {
  return [WRITING, STREAMING, LOADING, BROWSING_THE_INTERNET].includes(state);
};
